import React, { useState, useRef, useEffect, useContext } from 'react';

import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Avatar,
    Box,
    ButtonBase,
    ClickAwayListener,
    Divider,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Popper,
    Stack,
    Typography
} from '@mui/material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import MainCard from '../../../ui-component/cards/MainCard';
import Transitions from '../../../ui-component/extended/Transitions';

// assets
import { IconLogout, IconSettings } from '@tabler/icons';
import AuthContext from '../../../../auth/context/AuthContext';
import {useAuth} from '../../../../auth/hooks/useAuth';

// ==============================|| PROFILE MENU ||============================== //

const ProfileSection = () => {
    const currentUser = useContext(AuthContext);
    const theme = useTheme();
    const customization = useSelector((state) => state.common.customization);
    const history = useHistory();

    const { logout } = useAuth({
        middleware: 'guest',
    });

    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [open, setOpen] = useState(false);
    /**
     * anchorRef is used on different componets and specifying one type leads to other components throwing an error
     * */
    const anchorRef = useRef(null);

    const handleLogout = async () => {
        logout();
    };

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const handleListItemClick = (event, index, route = '') => {
        setSelectedIndex(index);
        handleClose(event);

        if (route && route !== '') {
            history.push(route);
        }
    };
    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const prevOpen = useRef(open);

    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }

        prevOpen.current = open;
    }, [open]);

    const userFullName = currentUser ? `${currentUser.firstname} ${currentUser.lastname}` : '';
    const userRole = currentUser ? currentUser.role : '';

    return (
        <>
            <ButtonBase onClick={handleToggle}
                        ref={anchorRef}
                        color="primary"
                        aria-controls={open ? 'menu-list-grow' : undefined}
                        aria-haspopup="true"
                        sx={{px: 3}}
                        focusRipple>
                <Stack direction="row"
                       spacing={2}
                       sx={{cursor: 'pointer'}}>
                    <Stack direction="column" sx={{marginTop: '8px'}}>
                            <Typography component="span" variant="subtitlewhite1" sx={{ fontWeight: 400, color: theme.palette.primaryLight }}>
                                {userFullName}
                            </Typography>
                        <Typography variant="subtitlewhite1" align="right">{userRole?.toUpperCase()}</Typography>
                    </Stack>
                    <Avatar
                        sx={{
                            ...theme.typography.mediumAvatar,
                            margin: '8px 0 8px 8px !important',
                            cursor: 'pointer'
                        }}
                        ref={anchorRef}
                        color="inherit"
                        aria-controls={open ? 'menu-list-grow' : undefined}
                        aria-haspopup="true"
                    />
                </Stack>
            </ButtonBase>
            <Popper
                placement="bottom-end"
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                popperOptions={{
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 14]
                            }
                        }
                    ]
                }}
            >
                {({ TransitionProps }) => (
                    <Transitions in={open} {...TransitionProps}>
                        <Paper>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                                    <Box sx={{ p: 2 }}>
                                        <Stack>
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <Typography component="span" variant="h4" sx={{ fontWeight: 400 }}>
                                                    {userFullName}
                                                </Typography>
                                            </Stack>
                                            <Typography variant="subtitle2">{userRole.toUpperCase()}</Typography>
                                        </Stack>
                                    </Box>
                                    <Divider />
                                    <PerfectScrollbar style={{ height: '100%', maxHeight: 'calc(100vh - 250px)', overflowX: 'hidden' }}>
                                        <Box sx={{ px: 2, pt: 1, pb: 2 }}>
                                            <List
                                                component="nav"
                                                sx={{
                                                    width: '100%',
                                                    maxWidth: 350,
                                                    minWidth: 300,
                                                    backgroundColor: theme.palette.background.paper,
                                                    borderRadius: '10px',
                                                    [theme.breakpoints.down('md')]: {
                                                        minWidth: '100%'
                                                    },
                                                    '& .MuiListItemButton-root': {
                                                        mt: 0
                                                    }
                                                }}
                                            >
                                                <ListItemButton
                                                    sx={{ borderRadius: `${customization.borderRadius}px` }}
                                                    selected={selectedIndex === 0}
                                                    onClick={(event) => handleListItemClick(event, 0, '/user/account-profile')}
                                                >
                                                    <ListItemIcon>
                                                        <IconSettings stroke={1.5} size="1.3rem" />
                                                    </ListItemIcon>
                                                    <ListItemText primary={<Typography variant="body2">Account Profile</Typography>} />
                                                </ListItemButton>
                                                <ListItemButton
                                                    sx={{ borderRadius: `${customization.borderRadius}px` }}
                                                    selected={selectedIndex === 4}
                                                    onClick={handleLogout}
                                                >
                                                    <ListItemIcon>
                                                        <IconLogout stroke={1.5} size="1.3rem" />
                                                    </ListItemIcon>
                                                    <ListItemText primary={<Typography variant="body2">Logout</Typography>} />
                                                </ListItemButton>
                                            </List>
                                        </Box>
                                    </PerfectScrollbar>
                                </MainCard>
                            </ClickAwayListener>
                        </Paper>
                    </Transitions>
                )}
            </Popper>
        </>
    );
};

export default ProfileSection;
